import { removeComponentProps } from '@hts/shared/utils';
import { HTMLAttributes, InputHTMLAttributes, forwardRef, memo, useCallback, useMemo } from 'react';
import validator from 'validator';
import styles from '../styles/TextInput.module.scss';

interface ITextInputProps {
  value: string;
  inputType?: 'text' | 'number';
  variant?: 'outlined' | 'contained';
  inputSize?: 'small' | 'medium';
  fullWidth?: boolean;
  showBackgroundPlaceholder?: boolean;
  placeholder?: string;
  isError?: boolean;
  firstSymbol?: string;
  disabled?: boolean;
  containerProps?: HTMLAttributes<HTMLDivElement>;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  onChangeValue: (value: string) => void;
}

export const TextInput = memo(
  forwardRef<HTMLInputElement, ITextInputProps>(
    (
      {
        inputType = 'text',
        value,
        placeholder,
        variant = 'contained',
        inputSize = 'medium',
        isError,
        showBackgroundPlaceholder = true,
        firstSymbol,
        leftContent,
        rightContent,
        disabled,
        fullWidth,
        containerProps,
        inputProps,
        onChangeValue,
      },
      ref
    ) => {
      const displayValue = firstSymbol ? `${firstSymbol}${value}` : value;

      const newMenuContainerProps = removeComponentProps(containerProps, ['className', 'style']);

      const inputClasses = [
        styles.container,
        variant === 'contained' ? styles.contained : styles.outlined,

        value.length === 0 &&
          variant === 'contained' &&
          showBackgroundPlaceholder &&
          styles.placeholder,

        inputSize === 'small' ? styles.small : styles.medium,
        isError && styles.error,
        firstSymbol && value.length === 0 && styles.withFirstSymbol,
      ];

      const containerStyles: React.CSSProperties = useMemo(
        () => ({
          ...containerProps?.style,
          width: fullWidth ? '100%' : containerProps?.style?.width || 'max-content',
          opacity: disabled ? 0.7 : 1,
        }),
        [containerProps?.style, fullWidth, disabled]
      );

      const inputStyles: React.CSSProperties = useMemo(
        () => ({
          opacity: disabled ? 0.7 : 1,
        }),
        [disabled]
      );

      const isCorrectNumberValue = useCallback(
        (inputValue: string) => !inputValue || validator.isInt(inputValue),
        []
      );

      const onChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
          let newValue = event.target.value;

          if (firstSymbol) {
            if (!newValue.startsWith(firstSymbol)) {
              newValue = firstSymbol + value;
            }

            const rawValue = newValue.slice(firstSymbol.length);

            if (inputType === 'number') {
              if (isCorrectNumberValue(rawValue)) {
                onChangeValue(rawValue);
              }
            } else {
              onChangeValue(rawValue);
            }

            return;
          }

          if (inputType === 'number') {
            if (isCorrectNumberValue(newValue)) {
              onChangeValue(newValue);
            }
          } else {
            onChangeValue(newValue);
          }
        },
        [onChangeValue, firstSymbol, inputType, value]
      );

      const onKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
          if (firstSymbol && event.key === 'ArrowLeft' && value.length === 0) {
            event.preventDefault();

            return;
          }
        },
        [firstSymbol, value.length]
      );

      return (
        <div
          className={[
            styles.container,
            isError && styles.errorContainer,
            containerProps?.className,
          ].join(' ')}
          style={containerStyles}
          {...newMenuContainerProps}
        >
          {leftContent}

          <input
            ref={ref}
            type={inputType}
            value={displayValue}
            style={inputStyles}
            disabled={disabled}
            className={inputClasses.join(' ')}
            placeholder={placeholder}
            onKeyDown={onKeyDown}
            onChange={onChange}
            {...inputProps}
          />

          {rightContent}
        </div>
      );
    }
  )
);
